import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { stringFormat, T } from 'src/shared/helpers';
import { showSuccessNotification } from '../../../../../../widgets/notificationToaster';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import ComposerRetargetAttributeDialog from '../../../configurator-composer/composer/dialogs/composerRetargetAttributeDialog';
import {
    getComposerSelectionDescendants,
    getComposerStructureSelectionRules,
    getComposerStructureSelectionSummary
} from '../../../configurator-composer/composer/helpers/composerBulkSelection';
import { getRetargetObjectsAndMaterials, retargetMapToActions } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { moveComposerAttributeNode } from '../../../configurator-composer/composer/helpers/composerTreeDnD';
import { requestComposerReveal } from '../composerReveal';
import { ComposerAttributeNode, ComposerAttributeOptionNode, ComposerAttributeType } from './composerAttributesModels';
import { optionsOfAttributes } from './optionSelectionCommands';
import { isConfirmedRun } from '../../confirmTwice';
import { ComposerPickerEntry, useComposerPicker } from '../composerPickerMenu';
import { ComposerAttributeMark } from '../../../configurator-composer/composer/components/composerAttributeTreeElement';

/** Si fondono solo attributi a opzioni: un attributo di aree non ha opzioni da mettere insieme. */
export const isMergeableAttribute = (attribute: ComposerAttributeNode) =>
    attribute.type === ComposerAttributeType.Attribute && attribute.attributeType !== MPlaza.AttributeType.CustomizableArea;

/**
 * I COMANDI DI UNA SELEZIONE DELLA STRUTTURA, UN POSTO SOLO (round 4, pacchetto F). Li usa il pannello
 * della selezione multipla e li usa il «…» di una riga scelta insieme ad altre: due porte, un motore —
 * come nello Scene Editor, dove il «…» di una riga selezionata vale per tutta la selezione.
 */
export const useStructureSelectionCommands = (attributes: ComposerAttributeNode[]) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [
        allAttributes,
        cloneAttributes,
        removeAttributes,
        updateAttribute,
        validateAttributes,
        selectAttributes,
        selectOptions,
        selectAreas,
        setAttributeFace,
        materials,
        objects
    ] = useComposerStore((store) => [
        store.attributes,
        store.cloneAttributes,
        store.removeAttributes,
        store.updateAttribute,
        store.validateAttributes,
        store.selectAttributes,
        store.selectOptions,
        store.selectAreas,
        store.setAttributeFace,
        store.sceneMaterialList,
        store.sceneObjectList
    ]);
    const selectionRules = getComposerStructureSelectionRules(attributes);
    const descendants = getComposerSelectionDescendants(attributes, allAttributes);
    const affectedRows = [...descendants, ...attributes];
    const removalSummary = getComposerStructureSelectionSummary(affectedRows);
    const actionableAttributes = [...attributes, ...descendants].filter((attribute) => attribute.type === ComposerAttributeType.Attribute);
    const actions = actionableAttributes.flatMap((attribute) => attribute.options.flatMap((option) => option.actions));
    // Un gruppo non entra in un altro gruppo; si fondono solo attributi a opzioni, almeno due.
    const groupable = attributes.filter((attribute) => attribute.type !== ComposerAttributeType.AttributeGroup);
    const canGroup = attributes.length > 1 && groupable.length === attributes.length;
    const mergeable = attributes.filter(isMergeableAttribute);
    const canMerge = attributes.length > 1 && mergeable.length === attributes.length;
    const selectionOptions = optionsOfAttributes(attributes);

    const land = (attribute: ComposerAttributeNode) => {
        void validateAttributes();
        selectAttributes([attribute]);
        selectOptions(null);
        selectAreas(null);
        setAttributeFace('settings');
        requestComposerReveal(attribute.id);
    };

    // «GROUP» SU UNA SELEZIONE (utente, 2026-10-15; prototipo): le righe vanno in un gruppo nuovo, al posto
    // della prima — è lo stesso spostamento del trascinamento dentro un gruppo, riga per riga.
    const groupSelection = () => {
        if (!canGroup) return;
        const current = useComposerStore.getState().attributes;
        const group = ComposerAttributeNodeAdapter.toGroupNode(ComposerAttributeNodeAdapter.createNewGroup());
        group.name = T._('New group', 'Admin');
        group.isNew = true;
        group.displayOrder = Math.max(-1, ...current.filter((attribute) => !attribute.parentID).map((attribute) => attribute.displayOrder)) + 1;
        let next = [...current, group];
        let anchor = next.find((attribute) => attribute.id === groupable[0].id);
        while (anchor?.parentID) anchor = next.find((attribute) => attribute.id === anchor?.parentID);
        if (anchor && anchor.id !== group.id) next = moveComposerAttributeNode(next, group.id, anchor.id, 'before');
        groupable.forEach((row) => {
            next = moveComposerAttributeNode(next, row.id, group.id, 'inside');
        });
        next.forEach((attribute) => {
            const before = current.find((candidate) => candidate.id === attribute.id);
            if (
                before &&
                before.parentID === attribute.parentID &&
                before.parentIDNumeric === attribute.parentIDNumeric &&
                before.parentType === attribute.parentType &&
                before.displayOrder === attribute.displayOrder
            )
                return;
            updateAttribute(attribute);
        });
        land(next.find((attribute) => attribute.id === group.id) ?? group);
    };

    // «MERGE» SENZA FINESTRA, CON UNA CONFERMA (utente, 2026-10-15): gli attributi scelti diventano uno solo,
    // al posto del primo e col suo nome, con tutte le loro opzioni. Gli originali si tolgono: è una
    // sostituzione, e per questo si chiede prima. Chi vuole tenerli fa prima Clone.
    const mergeNow = () => {
        const current = useComposerStore.getState().attributes;
        const sources = mergeable.map((attribute) => current.find((candidate) => candidate.id === attribute.id) ?? attribute);
        const first = sources[0];
        const merged = ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        const usedCodes = new Set(current.filter((attribute) => !sources.includes(attribute)).map((attribute) => attribute.code.trim().toLowerCase()));
        let code = first.code;
        for (let index = 1; usedCodes.has(code.trim().toLowerCase()); index++) code = `${first.code}-${index}`;
        merged.name = first.name;
        merged.code = code;
        merged.isNew = true;
        merged.parentID = first.parentID;
        merged.parentIDNumeric = first.parentIDNumeric;
        merged.parentType = first.parentType;
        merged.camera = first.camera;
        merged.previewType = first.previewType;
        merged.displayOrder = first.displayOrder;
        // Le opzioni come le fondeva la finestra di prima: codici resi unici con _1, _2, azioni con id nuovi,
        // collegamenti tolti (puntavano agli attributi di prima), la prima predefinita.
        const optionCodeCounts = new Map<string, number>();
        merged.options = sources
            .flatMap((attribute) => attribute.options)
            .map((option, index): ComposerAttributeOptionNode => {
                const count = optionCodeCounts.get(option.code) || 0;
                optionCodeCounts.set(option.code, count + 1);
                const optionID = MPlaza.generateUUID();
                return {
                    ...option,
                    id: optionID,
                    idNumeric: -1,
                    code: count > 0 ? `${option.code}_${count}` : option.code,
                    attributeID: merged.id,
                    isDefault: index === 0,
                    displayOrder: index,
                    links: [],
                    linkOperation: undefined,
                    actions: option.actions.map((action, actionIndex) => ({
                        ...action,
                        id: MPlaza.generateUUID(),
                        attributeID: merged.id,
                        optionID,
                        displayOrder: actionIndex,
                        isNew: true
                    }))
                };
            });
        removeAttributes(sources.map((attribute) => attribute.id));
        updateAttribute(merged);
        land(merged);
    };

    // MERGE SUBITO, SENZA FINESTRA (prototipo): fonde e lo dice con un avviso. Si annulla rimuovendo l'attributo nato.
    const mergeSelection = () => {
        if (!canMerge) return;
        const optionCount = mergeable.reduce((count, attribute) => count + attribute.options.length, 0);
        mergeNow();
        showSuccessNotification({
            message: stringFormat(
                T._('{0} attributes merged into “{1}”, with {2} options.', 'Admin'),
                mergeable.length.toString(),
                mergeable[0].name,
                optionCount.toString()
            )
        });
    };

    const duplicateSelection = () => {
        if (!selectionRules.canDuplicate) return;
        const cloned = cloneAttributes(affectedRows);
        const clonedSelection = cloned.slice(Math.max(0, cloned.length - attributes.length));
        selectAttributes(clonedSelection);
        selectOptions(null);
    };

    const setSelectionEnabled = (enabled: boolean) => {
        attributes
            .filter((attribute) => attribute.type !== ComposerAttributeType.PreSetContainer)
            .forEach((attribute) => updateAttribute({ ...attribute, enabled }));
    };

    const manageSelectedAttributeLinks = () => {
        if (!selectionRules.canManageLinks) return;
        selectAttributes([attributes[0]]);
        selectOptions(null);
        setAttributeFace('links');
    };

    const removeSelection = () => {
        const run = () => {
            removeAttributes(affectedRows.map((attribute) => attribute.id));
            selectAttributes(null);
            selectOptions(null);
        };
        // Dal bottone si chiede due volte, senza finestra (confirmTwice): arrivati qui, la domanda è già fatta.
        if (isConfirmedRun()) return run();
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={T._(
                    `Do you want remove ${removalSummary.attributes} attributes, ${removalSummary.groups} groups, ${removalSummary.presetConfigurations} pre-set configurations and ${removalSummary.steps} steps?`,
                    'Admin'
                )}
                buttons={[
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            run();
                        }
                    }
                ]}
            />
        );
    };

    const retargetSelection = () => {
        if (!selectionRules.canRetarget) return;
        const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
        showDialog(
            'composerRetargetAttribute',
            <ComposerRetargetAttributeDialog
                materialsList={materials}
                objectsList={objects}
                usedMaterialsList={materials.filter((material) => materialIds.includes(material.id))}
                usedObjectsList={objects.filter((object) => objectIds.includes(object.id) || objectIds.includes(object.name || ''))}
                onConfirm={(data) => {
                    closeDialog('composerRetargetAttribute');
                    if (!data) return;
                    const remapped = retargetMapToActions(actions, data.materialMap, data.objectMap);
                    actionableAttributes.forEach((attribute) =>
                        updateAttribute({
                            ...attribute,
                            options: attribute.options.map((option) => ({
                                ...option,
                                actions: option.actions.map((action) => remapped.find((candidate) => candidate.id === action.id) ?? action)
                            }))
                        })
                    );
                }}
            />
        );
    };

    return {
        selectionRules: { ...selectionRules, canGroup, canMerge, canImage: selectionOptions.length > 0 },
        selectionOptions,
        groupSelection,
        mergeSelection,
        duplicateSelection,
        setSelectionEnabled,
        manageSelectedAttributeLinks,
        removeSelection,
        retargetSelection
    };
};

/**
 * MOVE TO E COPY TO DELLA STRUTTURA (prototipo, utente 2026-10-20): un attributo, un passo o un preset — o una
 * selezione — dentro un gruppo o un passo, scelto da un menu di secondo livello come «Move to» dello Scene Editor.
 * È lo stesso spostamento del trascinamento dentro un ramo (`moveComposerAttributeNode`); Copy to prima clona, col
 * suo ramo, poi sposta la copia. Un gruppo non entra in un altro gruppo: per lui le due voci sono spente.
 */
export const useStructureMoveCopy = () => {
    const { openPicker, picker } = useComposerPicker();
    const [cloneAttributes, updateAttribute, validateAttributes, selectAttributes, selectOptions, selectAreas] = useComposerStore((store) => [
        store.cloneAttributes,
        store.updateAttribute,
        store.validateAttributes,
        store.selectAttributes,
        store.selectOptions,
        store.selectAreas
    ]);

    const place = (rowIds: string[], targetID: string | null) => {
        const current = useComposerStore.getState().attributes;
        let next = current;
        rowIds.forEach((id) => {
            next = moveComposerAttributeNode(next, id, targetID, targetID ? 'inside' : undefined);
        });
        next.forEach((attribute) => {
            const before = current.find((candidate) => candidate.id === attribute.id);
            if (
                before &&
                before.parentID === attribute.parentID &&
                before.parentIDNumeric === attribute.parentIDNumeric &&
                before.parentType === attribute.parentType &&
                before.displayOrder === attribute.displayOrder
            )
                return;
            updateAttribute(attribute);
        });
        const placed = useComposerStore.getState().attributes.filter((attribute) => rowIds.includes(attribute.id));
        void validateAttributes();
        selectAttributes(placed.length ? placed : null);
        selectOptions(null);
        selectAreas(null);
        if (placed[0]) requestComposerReveal(placed[0].id);
    };

    const movable = (rows: ComposerAttributeNode[]) => rows.filter((row) => row.type !== ComposerAttributeType.AttributeGroup);
    const canMoveOrCopy = (rows: ComposerAttributeNode[]) => rows.length > 0 && movable(rows).length === rows.length;

    const entriesFor = (rows: ComposerAttributeNode[], onPick: (targetID: string | null) => void, withTopLevel: boolean): ComposerPickerEntry[] => {
        const all = useComposerStore.getState().attributes;
        const ids = new Set(rows.map((row) => row.id));
        const containers = all.filter(
            (attribute) =>
                (attribute.type === ComposerAttributeType.AttributeGroup || attribute.type === ComposerAttributeType.AttributeStep) &&
                !ids.has(attribute.id) &&
                !rows.every((row) => row.parentID === attribute.id) &&
                // un passo non va in un passo, e niente entra in ciò che sta dentro di sé
                rows.every((row) => moveComposerAttributeNode(all, row.id, attribute.id, 'inside') !== all)
        );
        return [
            ...(withTopLevel && rows.some((row) => row.parentID)
                ? [{ id: '__top', label: T._('Top level', 'Admin'), caption: T._('Outside any group', 'Admin'), onPick: () => onPick(null) }]
                : []),
            ...containers.map((container) => ({
                id: container.id,
                label: container.name,
                icon: <ComposerAttributeMark attribute={container} />,
                onPick: () => onPick(container.id)
            }))
        ];
    };

    const openMoveTo = (anchor: HTMLElement | null | undefined, rows: ComposerAttributeNode[]) => {
        if (!canMoveOrCopy(rows)) return;
        openPicker(
            anchor,
            entriesFor(
                rows,
                (targetID) =>
                    place(
                        rows.map((row) => row.id),
                        targetID
                    ),
                true
            ),
            T._('Move to', 'Admin')
        );
    };

    const openCopyTo = (anchor: HTMLElement | null | undefined, rows: ComposerAttributeNode[]) => {
        if (!canMoveOrCopy(rows)) return;
        openPicker(
            anchor,
            entriesFor(
                rows,
                (targetID) => {
                    const all = useComposerStore.getState().attributes;
                    const descendants = getComposerSelectionDescendants(rows, all);
                    const cloned = cloneAttributes([...descendants, ...rows]);
                    const copies = cloned.slice(Math.max(0, cloned.length - rows.length));
                    place(
                        copies.map((copy) => copy.id),
                        targetID
                    );
                },
                false
            ),
            T._('Copy to', 'Admin')
        );
    };

    return { canMoveOrCopy, openMoveTo, openCopyTo, picker };
};
